/**
 * Single source of truth for how a `TranslationStatus` is PRESENTED to users.
 *
 * The status set is exactly FOUR values. The GraphQL/Smithy contract values
 * (`COMPLETE`, `IN_PROGRESS`, `FAILED`, `CANCELLED`) are NEVER renamed here —
 * they are the API contract. Only the display label and the badge colour live in
 * this module, so a wording change never touches the wire format:
 *
 *   COMPLETE   → "Complete"     (green)
 *   IN_PROGRESS→ "In progress"  (orange)
 *   FAILED     → "Failed"       (red)
 *   CANCELLED  → "Cancelled"    (brown)
 *
 * Status renders as a coloured `Badge` — a text pill with NO icon — which is what
 * the approved mockup showed (`.badge` classes: background colour + text colour,
 * nothing else). Cloudscape's `StatusIndicator` was the wrong primitive for this:
 * it ALWAYS renders an icon, with no prop to suppress it.
 *
 * HOW THE FOUR COLOURS ARE ACHIEVED — and why the semantic prop was not enough
 * ---------------------------------------------------------------------------
 * `Badge`'s semantic `color` prop accepts only `blue | grey | green | red |
 * severity-{critical,high,medium,low,neutral}` (verified against the installed
 * `@amzn/awsui-components-console/badge/interfaces.d.ts` — not assumed). So:
 *
 * - `green` and `red` come straight from the semantic prop, unchanged.
 * - ORANGE and BROWN are not in that list. There is no brown anywhere in the
 *   palette, and the only orange is `severity-medium`. That token is STILL not
 *   used here, for the reason this file has always given: the `severity-*` scale
 *   encodes security-finding severity, a translation lifecycle status is not a
 *   severity, and those tokens can be retuned independently of the general
 *   palette. Borrowing it would also make "In progress" and a future
 *   medium-severity finding indistinguishable by design intent.
 *   They are therefore painted by a local CSS module,
 *   `translationStatus.module.scss`, applied through the badge's `className`.
 *   That file documents the exact hexes, the Cloudscape tokens they came from,
 *   the dark-mode overrides, and the measured contrast ratios (all ≥ 4.5:1, i.e.
 *   WCAG AA, in both light and dark mode).
 *
 * The semantic `color` prop is still passed for the class-styled badges: it is
 * what paints them if the stylesheet is missing (e.g. a consumer that renders
 * this component without the CSS bundle), and it keeps each badge's fallback
 * close to its intended hue. The class overrides it with `!important` — see the
 * SCSS for why that is required rather than sloppy.
 *
 * ACCESSIBILITY (WCAG 1.4.1 — Use of Color): dropping the icon means colour is no
 * longer paired with a non-colour visual cue, so the VISIBLE TEXT LABEL is now the
 * sole thing keeping status from being conveyed by colour alone. Every badge
 * therefore renders its label as its child, and `getTranslationStatusDisplay`
 * always returns a non-empty `label` (unknown values fall back to the raw status
 * string rather than rendering an empty pill). Do not render a bare colour, and do
 * not replace the label with a tooltip or `aria-label` — it must stay visible.
 *
 * There is deliberately NO "Draft" entry. An early mockup showed one, but the
 * contract has no `DRAFT` value and the status set was confirmed as these four,
 * so Draft is not modelled anywhere in the UI.
 */
import React from 'react';
import Badge, { BadgeProps } from '@amzn/awsui-components-console/badge';
import { TranslationStatus } from 'src/generated/graphql';
import styles from './translationStatus.module.scss';

/**
 * The colour vocabulary this module exposes. Deliberately NOT `BadgeProps['color']`:
 * two of these (`orange`, `brown`) have no semantic counterpart and are realised
 * with a CSS class, so callers and tests reason about the INTENDED colour rather
 * than about how it happens to be painted.
 */
export type TranslationStatusColor =
  | 'green'
  | 'orange'
  | 'red'
  | 'brown'
  | 'grey';

export interface TranslationStatusDisplay {
  /**
   * User-visible label. Always non-empty — it is the accessible, non-colour
   * carrier of the status now that no icon is rendered.
   */
  readonly label: string;
  /** Intended badge colour. Drives colour only, never an icon. */
  readonly color: TranslationStatusColor;
}

/**
 * How each intended colour is actually painted: a semantic `Badge` colour (the
 * fallback if the stylesheet is absent) plus, for the two colours Cloudscape has
 * no token for, the CSS-module class that overrides it.
 */
const BADGE_PRESENTATION: Record<
  TranslationStatusColor,
  { readonly badgeColor: BadgeProps['color']; readonly className?: string }
> = {
  green: { badgeColor: 'green' },
  red: { badgeColor: 'red' },
  grey: { badgeColor: 'grey' },
  // `blue`/`grey` are the pre-change colours, kept purely as no-stylesheet
  // fallbacks; the class is what paints the orange and brown.
  orange: { badgeColor: 'blue', className: styles.orange },
  brown: { badgeColor: 'grey', className: styles.brown },
};

/**
 * The complete status → presentation mapping. `Record<TranslationStatus, …>` is
 * exhaustive by construction: adding a value to the GraphQL enum without adding
 * it here is a compile error, and an entry here that is not in the enum is too —
 * so this cannot drift into orphaned or missing statuses.
 */
const TRANSLATION_STATUS_DISPLAY: Record<
  TranslationStatus,
  TranslationStatusDisplay
> = {
  [TranslationStatus.Complete]: { label: 'Complete', color: 'green' },
  [TranslationStatus.InProgress]: { label: 'In progress', color: 'orange' },
  [TranslationStatus.Failed]: { label: 'Failed', color: 'red' },
  [TranslationStatus.Cancelled]: { label: 'Cancelled', color: 'brown' },
};

/**
 * Resolves the display metadata for a status.
 *
 * Accepts a plain `string` because some payloads type status loosely (e.g. the
 * Smithy `Translation.status` member is a String, not the enum). Unknown values
 * degrade gracefully to the raw value with a neutral colour rather than throwing
 * or rendering nothing — the raw value keeps the badge from being colour-only.
 */
export function getTranslationStatusDisplay(
  status: string,
): TranslationStatusDisplay {
  return (
    TRANSLATION_STATUS_DISPLAY[status as TranslationStatus] ?? {
      label: status,
      color: 'grey',
    }
  );
}

/** User-visible label for a status value. */
export function getTranslationStatusLabel(status: string): string {
  return getTranslationStatusDisplay(status).label;
}

interface TranslationStatusIndicatorProps {
  readonly status: string;
}

/**
 * Renders a status as a coloured Cloudscape `Badge` with its label and no icon.
 *
 * The label child is required for accessibility — see the module doc comment.
 */
export const TranslationStatusIndicator: React.FC<
  TranslationStatusIndicatorProps
> = ({ status }) => {
  const { label, color } = getTranslationStatusDisplay(status);
  const { badgeColor, className } = BADGE_PRESENTATION[color];
  return (
    <Badge color={badgeColor} className={className}>
      {label}
    </Badge>
  );
};
